import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Quote } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

export default function Testimonials() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ body: '', role: '', anonymous: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get('/testimonials'); setItems(data); }
    finally { setLoading(false); }
  })(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.role) delete payload.role;
      await api.post('/testimonials', payload);
      toast.success(t.testimonials.pending);
      setShowForm(false);
      setForm({ body: '', role: '', anonymous: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.testimonials.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{t.testimonials.subtitle}</p>

      <div className="mt-10 flex justify-end">
        <button onClick={() => { if (!user) navigate('/login'); else setShowForm(!showForm); }}
          data-testid="testimonial-add-btn"
          className="px-5 py-2.5 rounded-full border border-brand-gold text-foreground font-medium hover:bg-secondary transition-colors" style={{ borderColor: '#C5A059' }}>
          {t.testimonials.add}
        </button>
      </div>

      {showForm && user && (
        <form onSubmit={submit} className="mt-6 p-8 rounded-3xl border border-border bg-card space-y-5" data-testid="testimonial-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.testimonials.body_label}</label>
            <textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="testimonial-body-input" required minLength={20} maxLength={1000}
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.testimonials.role_label}</label>
              <input type="text" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="testimonial-role-input" className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
            </div>
            <label className="inline-flex items-center gap-2 self-end pb-3 cursor-pointer">
              <input type="checkbox" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} data-testid="testimonial-anon" />
              <span className="text-sm">{t.testimonials.anonymous_label}</span>
            </label>
          </div>
          <button type="submit" disabled={submitting} data-testid="testimonial-submit-btn"
            className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
            {submitting ? t.common.loading : t.testimonials.submit}
          </button>
        </form>
      )}

      <div className="mt-16" data-testid="testimonials-list">
        {loading ? <p className="text-muted-foreground">{t.common.loading}</p> : items.length === 0 ? (
          <p className="text-muted-foreground text-center py-16">{t.testimonials.empty}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((t2, i) => (
              <div key={t2.id} data-testid={`testimonial-${t2.id}`} className="p-8 rounded-3xl border border-border bg-card relative">
                <Quote size={28} className="text-brand-gold-dark opacity-40 absolute top-6 left-6" strokeWidth={1.5} />
                <p className="pt-10 text-lg leading-relaxed text-foreground/90">{t2.body}</p>
                <div className="mt-6 pt-4 border-t border-border">
                  <div className="font-medium">{t2.author_name}</div>
                  {t2.role && <div className="text-xs text-muted-foreground mt-0.5">{t2.role}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
